/**
 * cvMenuParser.js
 *
 * Shared helpers for parsing the GPT-4o vision model response returned by the
 * computervision Cloud Function (POST /computervision/uploadFile).
 *
 * The endpoint returns:
 *   { jsonDataArray: ["...markdown json string...", ...] }
 *
 * Each string is a GPT-4o response that may be:
 *   a) A raw JSON string
 *   b) A markdown code-fence block: ```json\n{...}\n```
 *
 * For multi-page PDFs, the array has one entry per page.  mergePageResults
 * collapses them into a single menu + items object.
 */

/**
 * Strip markdown code fences and parse a GPT-4o response string into a JS
 * object.  Returns null if the string cannot be parsed.
 *
 * @param {string} rawText
 * @returns {object|null}
 */
export function parseGPTResponse(rawText) {
  if (!rawText || typeof rawText !== 'string') return null;
  try {
    // Match ```json ... ``` or ``` ... ```
    const codeBlockMatch = rawText.match(/```(?:json)?\s*([\s\S]*?)```/i);
    const jsonStr = codeBlockMatch ? codeBlockMatch[1].trim() : rawText.trim();
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}

/**
 * Merge an array of per-page GPT-4o response strings into a single result.
 * Takes the first non-empty value for top-level fields (menu_name, avail,
 * venue) and concatenates all items arrays.
 *
 * @param {string[]} jsonDataArray
 * @returns {{ menuName: string, avail: string, venue: string, items: object[] }}
 */
export function mergePageResults(jsonDataArray) {
  let menuName = '';
  let avail = '';
  let venue = '';
  const allItems = [];

  for (const raw of (jsonDataArray || [])) {
    const parsed = parseGPTResponse(raw);
    if (!parsed) continue;
    if (!menuName && parsed.menu_name) menuName = parsed.menu_name;
    if (!avail && parsed.avail) avail = parsed.avail;
    if (!venue && parsed.venue) venue = parsed.venue;
    if (Array.isArray(parsed.items)) {
      allItems.push(...parsed.items);
    }
  }

  return { menuName, avail, venue, items: allItems };
}
